import { motion } from 'framer-motion';
import { Target, Eye, Users, Award, CheckCircle } from 'lucide-react';

export const About = () => {
  const values = [
    {
      icon: <CheckCircle className="w-8 h-8" />,
      title: 'Quality Assurance',
      description: 'We deliver only the best quality generators and services',
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: 'Customer First',
      description: 'Your satisfaction and success is our priority',
    },
    {
      icon: <Award className="w-8 h-8" />,
      title: 'Expert Team',
      description: 'Certified professionals with extensive experience',
    },
  ];

  const achievements = [
    { number: '15+', label: 'Years Experience' },
    { number: '500+', label: 'Projects Completed' },
    { number: '200+', label: 'Happy Clients' },
    { number: '24/7', label: 'Support Available' },
  ];

  return (
    <div className="pt-20" data-testid="about-page">
      <section className="py-20 bg-gradient-to-br from-[#1B365D] to-[#2563eb] text-white noise-overlay" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6" data-testid="about-title">
              About Power Tech Engineers
            </h1>
            <p className="text-lg sm:text-xl text-slate-200 max-w-3xl mx-auto">
              Your trusted partner in diesel generator solutions for over 15 years
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-white" data-testid="company-story-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl lg:text-4xl font-bold text-[#1B365D] mb-6" data-testid="our-story-title">
                Our Story
              </h2>
              <div className="space-y-4 text-slate-600 leading-relaxed">
                <p>
                  Power Tech Engineers was founded with a simple mission: to provide reliable and efficient power solutions to businesses and industries across Karnataka. Since our inception, we have been committed to delivering excellence in every project we undertake.
                </p>
                <p>
                  With over 15 years of experience in the industry, we have successfully completed more than 500 projects, ranging from small residential installations to large industrial setups. Our expertise spans across diesel generator sales, installation, commissioning, and comprehensive maintenance services.
                </p>
                <p>
                  Based in Mysore, we serve clients throughout Karnataka and neighboring states, providing them with cutting-edge power backup solutions tailored to their specific needs. Our team of certified engineers and technicians ensures that every installation meets the highest standards of quality and safety.
                </p>
                <p>
                  Today, Power Tech Engineers stands as a trusted name in the power generation industry, known for our reliability, technical expertise, and unwavering commitment to customer satisfaction.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <img
                src="https://images.unsplash.com/photo-1601119462238-d7f347e41844?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwyfHx0ZWNobmljaWFuJTIwcmVwYWlyaW5nJTIwaW5kdXN0cmlhbCUyMGdlbmVyYXRvciUyMHNhZmV0eSUyMGdlYXJ8ZW58MHx8fHwxNzcxOTEzMjM3fDA&ixlib=rb-4.1.0&q=85"
                alt="Team working on generator"
                className="rounded-sm shadow-2xl"
                data-testid="about-image"
              />
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-50" data-testid="mission-vision-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-white p-10 rounded-sm border border-slate-200 shadow-md"
              data-testid="mission-card"
            >
              <div className="text-[#F97316] mb-4">
                <Target className="w-12 h-12" />
              </div>
              <h3 className="text-2xl font-bold text-[#1B365D] mb-4">Our Mission</h3>
              <p className="text-slate-600 leading-relaxed">
                To empower businesses with reliable, efficient, and sustainable power solutions through high-quality diesel generators and exceptional service. We strive to be the most trusted partner in ensuring uninterrupted power supply for our clients, enabling them to focus on their core operations without worrying about power failures.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-white p-10 rounded-sm border border-slate-200 shadow-md"
              data-testid="vision-card"
            >
              <div className="text-[#F97316] mb-4">
                <Eye className="w-12 h-12" />
              </div>
              <h3 className="text-2xl font-bold text-[#1B365D] mb-4">Our Vision</h3>
              <p className="text-slate-600 leading-relaxed">
                To become the leading provider of power generation solutions in South India, recognized for our innovation, reliability, and customer-centric approach. We envision a future where every business has access to dependable backup power, and we aim to be the company that makes this vision a reality through continuous improvement and technological advancement.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-[#1B365D] mb-4" data-testid="values-title">
              Our Core Values
            </h2>
            <p className="text-lg text-slate-600">The principles that guide everything we do</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center p-8 bg-slate-50 rounded-sm border border-slate-200 hover:border-[#F97316] hover:shadow-lg transition-all"
                data-testid={`value-card-${index}`}
              >
                <div className="text-[#F97316] flex justify-center mb-4">{value.icon}</div>
                <h3 className="text-xl font-bold text-[#1B365D] mb-3">{value.title}</h3>
                <p className="text-slate-600">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#1B365D] text-white noise-overlay" data-testid="achievements-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-4" data-testid="achievements-title">
              Our Achievements
            </h2>
            <p className="text-lg text-slate-300">Numbers that speak for our excellence</p>
          </motion.div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {achievements.map((achievement, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
                data-testid={`achievement-${index}`}
              >
                <div className="text-4xl lg:text-5xl font-bold text-[#F97316] mb-2">
                  {achievement.number}
                </div>
                <div className="text-slate-300 font-medium">{achievement.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-50" data-testid="why-trust-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-[#1B365D] mb-6" data-testid="why-trust-title">
              Why Customers Trust Us
            </h2>
            <div className="space-y-4 text-slate-600 text-left leading-relaxed">
              <div className="flex items-start gap-4">
                <CheckCircle className="w-6 h-6 text-[#F97316] flex-shrink-0 mt-1" />
                <p>
                  <strong>Proven Track Record:</strong> Over 15 years of successful installations and satisfied customers across multiple industries.
                </p>
              </div>
              <div className="flex items-start gap-4">
                <CheckCircle className="w-6 h-6 text-[#F97316] flex-shrink-0 mt-1" />
                <p>
                  <strong>Expert Team:</strong> Our certified engineers and technicians undergo regular training to stay updated with the latest technologies and best practices.
                </p>
              </div>
              <div className="flex items-start gap-4">
                <CheckCircle className="w-6 h-6 text-[#F97316] flex-shrink-0 mt-1" />
                <p>
                  <strong>Quality Products:</strong> We only supply generators from reputed manufacturers with proven reliability and performance.
                </p>
              </div>
              <div className="flex items-start gap-4">
                <CheckCircle className="w-6 h-6 text-[#F97316] flex-shrink-0 mt-1" />
                <p>
                  <strong>Comprehensive Support:</strong> From initial consultation to after-sales service, we provide end-to-end support for all your power needs.
                </p>
              </div>
              <div className="flex items-start gap-4">
                <CheckCircle className="w-6 h-6 text-[#F97316] flex-shrink-0 mt-1" />
                <p>
                  <strong>Competitive Pricing:</strong> We offer the best value for money with transparent pricing and flexible payment options.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};